import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Divider,
} from '@mui/material';
import {
  Launch as LaunchIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';

const Eloguessr: React.FC = () => {
  return (
    <Box>
      {/* Header avec titre et image */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <img
          src="/Twitch/eloguessr.png"
          alt="Logo Eloguessr"
          style={{
            width: '64px',
            height: '64px',
            objectFit: 'contain',
            filter: 'drop-shadow(2px 2px 4px rgba(167, 139, 250, 0.3))',
          }}
        />
        <Typography
          variant="h4"
          component="h2"
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Eloguessr
        </Typography>
      </Box>

      {/* Statut et date */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            2023 - En cours
          </Typography>
        </Box>
      </Box>

      {/* Description */}
      <Typography
        variant="body1"
        sx={{ mb: 3, lineHeight: 1.6 }}
      >
        Plateforme web interactive permettant aux streamers de visionner des clips de joueurs 
        sur différents jeux compétitifs et de deviner leur rang. La particularité : le streamer 
        joue en compétition directe contre son chat Twitch, avec un système de classement 
        en temps réel.
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* Fonctionnalités principales */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Fonctionnalités principales
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
          gap: 2 
        }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Compétition streamer vs chat
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Classement temps réel
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Interface streamer dédiée
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Intégration chat Twitch
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Technologies */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Technologies utilisées
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {['Express', 'TypeScript', 'React', 'Twitch API', 'WebSocket', 'Node.js', 'MongoDB'].map((tech) => (
            <Chip
              key={tech}
              label={tech}
              variant="filled"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.1) 0%, rgba(20, 184, 166, 0.1) 100%)',
                color: '#a78bfa',
                border: '1px solid rgba(167, 139, 250, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.2) 0%, rgba(20, 184, 166, 0.2) 100%)',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Défis techniques */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Défis techniques relevés
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Gestion de rooms multi-joueurs :</strong> Architecture permettant la création 
          et la gestion simultanée de plusieurs salles de jeu indépendantes, chacune avec 
          ses propres participants, scores et état de partie en temps réel.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Support multi-jeux :</strong> Système modulaire capable de gérer différents 
          types de jeux compétitifs (Valorant, League of Legends, etc.).
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Disponibilité 24h/24 :</strong> Infrastructure robuste avec monitoring automatique, 
          gestion des pannes et système de redémarrage pour assurer un service continu sans 
          interruption, même en cas de pic de trafic.
        </Typography>
        <Typography variant="body2">
          <strong>Personnalisation avancée :</strong> Système de thèmes dynamiques permettant 
          à chaque streamer de customiser l'apparence pour s'adapter 
          à leur identité visuelle et direction artistique.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Liens actions */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<LaunchIcon />}
          href="https://eloguessr.fun"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #8b5cf6 0%, #0f766e 100%)',
            },
          }}
        >
          Visiter eloguessr.fun
        </Button>
      </Box>
    </Box>
  );
};

export default Eloguessr;

